"use client";

import React from "react";
import { formatDistanceToNow } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

// Types
import type { TabContentProps } from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { Time_Entries } from "@/src/gql/graphql";

/**
 * Time Entry Activity Timeline Tab Component
 *
 * Displays a timeline of activities related to the time entry,
 * including creation, updates, approval status, billable status, and sync status.
 */
export function TimeEntryActivityTimelineTab({ item }: TabContentProps<Time_Entries>) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Communication Timeline</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {/* Time Entry Created */}
          <div className="flex items-start gap-3 p-3 bg-card-50 rounded-lg">
            <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
            <div>
              <p className="text-sm font-medium text-card-foreground">Time Entry Created</p>
              <p className="text-xs text-gray-500">
                {item.created_at
                  ? formatDistanceToNow(new Date(item.created_at), { addSuffix: true })
                  : "Unknown"}
              </p>
            </div>
          </div>

          {/* Time Entry Updated */}
          {item.updated_at && item.updated_at !== item.created_at && (
            <div className="flex items-start gap-3 p-3 bg-card-50 rounded-lg">
              <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2"></div>
              <div>
                <p className="text-sm font-medium text-card-foreground">Time Entry Updated</p>
                <p className="text-xs text-gray-500">
                  {formatDistanceToNow(new Date(item.updated_at), { addSuffix: true })}
                </p>
              </div>
            </div>
          )}

          {/* Time Entry Approved */}
          {item.is_approved && (
            <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
              <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
              <div>
                <p className="text-sm font-medium text-card-foreground">Time Entry Approved</p>
                <p className="text-xs text-gray-500">
                  {item.updated_at
                    ? formatDistanceToNow(new Date(item.updated_at), { addSuffix: true })
                    : "Unknown"}
                </p>
              </div>
            </div>
          )}

          {/* Billable Status */}
          {item.entry_type === "billable" && (
            <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
              <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
              <div>
                <p className="text-sm font-medium text-card-foreground">Marked as Billable</p>
                <p className="text-xs text-gray-500">Rate: ${item.billable_rate || "N/A"}/hour</p>
              </div>
            </div>
          )}

          {/* Sync Status */}
          {item.sync_status && item.sync_status !== "pending" && (
            <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg">
              <div className="w-2 h-2 bg-purple-500 rounded-full mt-2"></div>
              <div>
                <p className="text-sm font-medium text-card-foreground">Synced to QuickBooks</p>
                <p className="text-xs text-gray-500">Status: {item.sync_status}</p>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
